import { Link, NavLink, Outlet } from 'react-router';
import { Moon, ShoppingBag, Sun, User } from 'lucide-react';
import { Button } from '@dzseguidores/ui';
import { useTheme } from '@/lib/utils';
import { useIsAuthenticated } from '@/lib/auth';
import { useCartStore } from '@/stores/cart-store';
import { PixelTracker } from '@/components/analytics/pixel-tracker';
import { useAnalyticsTracker } from '@/hooks/use-analytics-tracker';

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `text-sm transition ${isActive ? 'text-fg font-medium' : 'text-muted hover:text-fg'}`;

export function StorefrontLayout() {
  const { dark, toggle } = useTheme();
  const itemCount = useCartStore((s) => s.packageCount());
  const loggedIn = useIsAuthenticated();
  useAnalyticsTracker();

  return (
    <div className="flex min-h-screen flex-col">
      <PixelTracker />
      <header className="sticky top-0 z-40 border-b border-border glass">
        <div className="container-page flex h-16 items-center justify-between gap-4">
          <Link
            to="/"
            className="font-display text-lg font-semibold tracking-tight"
            title="DZ Seguidores — comprar seguidores e curtidas Instagram e TikTok"
          >
            DZ Seguidores
          </Link>
          <nav className="hidden items-center gap-6 md:flex" aria-label="Principal">
            <NavLink to="/instagram" className={navLinkClass}>
              Instagram
            </NavLink>
            <NavLink to="/tiktok" className={navLinkClass}>
              TikTok
            </NavLink>
            <NavLink to="/catalogo" className={navLinkClass}>
              Catálogo
            </NavLink>
            <NavLink to="/conta/pedidos" className={navLinkClass}>
              Minha conta
            </NavLink>
          </nav>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggle}
              className="rounded-lg p-2 text-muted hover:bg-surface hover:text-fg"
              aria-label="Alternar tema"
            >
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <Link
              to="/carrinho"
              className="relative rounded-lg p-2 text-muted hover:bg-surface hover:text-fg"
              aria-label="Carrinho"
            >
              <ShoppingBag className="h-4 w-4" />
              {itemCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--color-accent)] px-1 text-[10px] font-semibold text-[var(--color-accent-fg)]">
                  {itemCount}
                </span>
              )}
            </Link>
            {loggedIn ? (
              <Link to="/conta/pedidos" className="hidden sm:block">
                <Button variant="ghost" size="sm">
                  <User className="mr-1.5 h-4 w-4" />
                  Conta
                </Button>
              </Link>
            ) : (
              <Link to="/login" className="hidden sm:block">
                <Button variant="ghost" size="sm">
                  <User className="mr-1.5 h-4 w-4" />
                  Entrar
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-border py-10">
        <div className="container-page grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="font-display font-semibold text-fg">DZ Seguidores</p>
            <p className="mt-2 text-sm text-muted">
              Compre seguidores e curtidas para Instagram e TikTok com entrega monitorada e PIX.
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold text-fg">Instagram</p>
            <ul className="mt-3 space-y-2 text-sm text-muted">
              <li>
                <Link to="/instagram/seguidores" className="hover:text-fg">
                  Comprar seguidores Instagram
                </Link>
              </li>
              <li>
                <Link to="/instagram/curtidas" className="hover:text-fg">
                  Comprar curtidas Instagram
                </Link>
              </li>
              <li>
                <Link to="/instagram" className="hover:text-fg">
                  Todos os pacotes Instagram
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <p className="text-sm font-semibold text-fg">TikTok</p>
            <ul className="mt-3 space-y-2 text-sm text-muted">
              <li>
                <Link to="/tiktok/seguidores" className="hover:text-fg">
                  Comprar seguidores TikTok
                </Link>
              </li>
              <li>
                <Link to="/tiktok/curtidas" className="hover:text-fg">
                  Comprar curtidas TikTok
                </Link>
              </li>
              <li>
                <Link to="/tiktok" className="hover:text-fg">
                  Todos os pacotes TikTok
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <p className="text-sm font-semibold text-fg">Loja</p>
            <ul className="mt-3 space-y-2 text-sm text-muted">
              <li>
                <Link to="/catalogo" className="hover:text-fg">
                  Catálogo completo
                </Link>
              </li>
              <li>
                <Link to="/conta/suporte" className="hover:text-fg">
                  Suporte
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="container-page mt-10 border-t border-border pt-6 text-sm text-muted">
          <p>© {new Date().getFullYear()} DZ Seguidores. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
